import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import App from './App'
import { queryClient } from './lib/api/query-client'
import { installGlobalErrorReporting } from './lib/api/error-reporter'
import './index.css'
import './global.css'

// ── Global error reporting (uncaught errors / unhandled rejections) ─────────
installGlobalErrorReporting();

// ── Pre-router URL normalization ─────────────────────────────────────────────
// Strips /en prefix before React Router mounts to fix locale redirect issues.
// This prevents the app from booting on /en when the browser has stale state.
function normalizeLocaleUrl() {
  const { pathname, search, hash } = window.location;
  const localePrefix = '/en';

  // Check if pathname starts with the locale prefix
  if (pathname.startsWith(localePrefix)) {
    let newPath = pathname.slice(localePrefix.length);

    // Fix root case: /en -> /
    if (newPath === '') {
      newPath = '/';
    }

    // Reconstruct and replace URL without adding to history
    const newUrl = newPath + search + hash;
    if (newUrl !== pathname + search + hash) {
      window.history.replaceState(null, '', newUrl);
    }
  }
}

// Run normalization before app mounts
if (typeof window !== 'undefined') {
  normalizeLocaleUrl();
}

function ScrollToTop() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ScrollToTop />
        <App />
      </BrowserRouter>
      {import.meta.env.DEV && <ReactQueryDevtools />}
    </QueryClientProvider>
  </React.StrictMode>,
)
